import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { format } from 'date-fns';
import { getQuarter } from '@/helpers/date';
import { getExecutiveInsights } from '@/api/insights';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { calculatePercent } from '@/components/insights/helpers';

export const useExecutiveInsights = defineStore('executiveInsights', () => {
  const applicationsReceived = ref(0);
  const overallPassthroughRate = ref(0);
  const hiresMade = ref(0);
  const timeToHire = ref(0);
  const offerAcceptanceRate = ref(0);
  const dropOffRate = ref(0);
  const genderDistribution = ref({
    femaleAppsPercent: 0,
    maleAppsPercent: 0,
    undeterminedAppsPercent: 0,
    femaleHiredPercent: 0,
    maleHiredPercent: 0,
    undeterminedHiredPercent: 0,
  });
  const sourceDistribution = ref({
    internalAppsPercent: 0,
    internalHiredPercent: 0,
    referralAppsPercent: 0,
    referralHiredPercent: 0,
    outboundAppsPercent: 0,
    outboundHiredPercent: 0,
    inboundAppsPercent: 0,
    inboundHiredPercent: 0,
    otherAppsPercent: 0,
    otherHiredPercent: 0,
  });
  const flags = ref({});
  const labelsForLoading = ref({});
  const error = ref(null);

  const isEmptyData = computed(() => applicationsReceived.value === 0
    && overallPassthroughRate.value === 0
    && hiresMade.value === 0
    && timeToHire.value === 0
    && offerAcceptanceRate.value === 0
    && dropOffRate.value === 0
    && Object.values(genderDistribution.value).every((value) => value === 0)
    && Object.values(sourceDistribution.value).every((value) => value === 0));

  const setData = ({
    total_applications: newApplicationsReceived,
    total_hires: newHiresMade,
    median_hire_time_days: newTimeToHire,
    offer_acceptance_rate: newOfferAcceptanceRate,
    gender_metrics: genderMetrics,
    source_metrics: sourceMetrics,
    dropoff_rate: newDropOffRate,
    pass_through_rate: newOverallPassthroughRate,
  } = {}) => {
    overallPassthroughRate.value = newOverallPassthroughRate * 100 || 0;
    dropOffRate.value = newDropOffRate * 100 || 0;
    applicationsReceived.value = newApplicationsReceived || 0;
    hiresMade.value = newHiresMade || 0;
    timeToHire.value = newTimeToHire || 0;
    offerAcceptanceRate.value = newOfferAcceptanceRate || 0;

    const { hires: genderHires, applications: genderApplications } = genderMetrics || {};

    const { female: femaleHires, male: maleHires, unknown: unknownHires } = genderHires || {};
    const {
      female: femaleApplications,
      male: maleApplications,
      unknown: unknownApplications,
    } = genderApplications || {};

    const newFemaleAppsPercent = calculatePercent(
      femaleApplications,
      newApplicationsReceived,
      true,
    );
    const newMaleAppsPercent = calculatePercent(maleApplications, newApplicationsReceived, true);
    const newUndeterminedAppsPercent = calculatePercent(
      unknownApplications,
      newApplicationsReceived,
      true,
    );
    const newFemaleHiredPercent = calculatePercent(femaleHires, newHiresMade, true);
    const newMaleHiredPercent = calculatePercent(maleHires, newHiresMade, true);
    const newUndeterminedHiredPercent = calculatePercent(unknownHires, newHiresMade, true);

    genderDistribution.value = {
      femaleAppsPercent: newFemaleAppsPercent,
      maleAppsPercent: newMaleAppsPercent,
      undeterminedAppsPercent: newUndeterminedAppsPercent,
      femaleHiredPercent: newFemaleHiredPercent,
      maleHiredPercent: newMaleHiredPercent,
      undeterminedHiredPercent: newUndeterminedHiredPercent,
    };

    const { hires: sourceHires, applications: sourceApplications } = sourceMetrics || {};
    const {
      internal: internalHires,
      referrals: referralHires,
      outbound: outboundHires,
      inbound: inboundHires,
      other: otherHires,
    } = sourceHires || {};
    const {
      internal: internalApplications,
      referrals: referralApplications,
      outbound: outboundApplications,
      inbound: inboundApplications,
      other: otherApplications,
    } = sourceApplications || {};
    const newInternalAppsPercent = calculatePercent(
      internalApplications,
      newApplicationsReceived,
      true,
    );
    const newInternalHiredPercent = calculatePercent(internalHires, newHiresMade, true);
    const newReferralAppsPercent = calculatePercent(
      referralApplications,
      newApplicationsReceived,
      true,
    );
    const newReferralHiredPercent = calculatePercent(referralHires, newHiresMade, true);
    const newOutboundAppsPercent = calculatePercent(
      outboundApplications,
      newApplicationsReceived,
      true,
    );
    const newOutboundHiredPercent = calculatePercent(outboundHires, newHiresMade, true);
    const newInboundAppsPercent = calculatePercent(
      inboundApplications,
      newApplicationsReceived,
      true,
    );
    const newInboundHiredPercent = calculatePercent(inboundHires, newHiresMade, true);
    const newOtherAppsPercent = calculatePercent(otherApplications, newApplicationsReceived, true);
    const newOtherHiredPercent = calculatePercent(otherHires, newHiresMade, true);
    sourceDistribution.value = {
      internalAppsPercent: newInternalAppsPercent,
      internalHiredPercent: newInternalHiredPercent,
      referralAppsPercent: newReferralAppsPercent,
      referralHiredPercent: newReferralHiredPercent,
      outboundAppsPercent: newOutboundAppsPercent,
      outboundHiredPercent: newOutboundHiredPercent,
      inboundAppsPercent: newInboundAppsPercent,
      inboundHiredPercent: newInboundHiredPercent,
      otherAppsPercent: newOtherAppsPercent,
      otherHiredPercent: newOtherHiredPercent,
    };
  };

  const setFlags = (newFlags) => {
    flags.value = newFlags;
  };

  const setLabelsForLoading = (value) => {
    labelsForLoading.value = value;
  };

  const loadExecutiveInsights = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    const skeletonLoadersTimeout = setTimeout(() => {
      setLabelsForLoading({
        // NOTE: in exec insights widgetLabels is an object, other insights is an array
        widgetLabels: {
          'time-to-hire': 'Time to Hire vs Benchmark',
          'offer-acceptance': 'Offer Acceptance Rate vs Benchmark',
          'drop-off': 'Drop-off Rate vs Benchmark',
          'gender-distribution': 'What is the gender distribution of our candidates?',
          'source-distribution': 'What are our best sources?',
        },
        tableLabel: '',
      });
      // add intermediate loading state to filter bar
      useAppLoading().setLoading({ halfTopBarLoading: false });
    }, 4000);

    useAppLoading().setLoading({
      pageLoading: true, halfTopBarLoading: true,
    });

    try {
      const {
        data: newData = {},
        flags: newFlags = {},
      } = await getExecutiveInsights({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      clearTimeout(skeletonLoadersTimeout);
      setLabelsForLoading({});

      setData(newData);
      setFlags(newFlags);
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load executive insights - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
        halfTopBarLoading: false,
      });
    }
  };

  return {
    // State
    applicationsReceived,
    overallPassthroughRate,
    hiresMade,
    timeToHire,
    offerAcceptanceRate,
    dropOffRate,
    genderDistribution,
    sourceDistribution,
    flags,
    labelsForLoading,
    // Getters
    isEmptyData,
    // Actions
    setData,
    setFlags,
    loadExecutiveInsights,
  };
});
